/**
 * useCreateUpdateAddressFormMutation Hook
 * Handles create, update, and delete address mutations
 * @see mobile: module-customer/src/modules/addresses/hooks/use-handle-address-form-mutation.hooks.ts
 */

'use client';

import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApolloError } from '@apollo/client';
import {
    type CustomerAddress,
    type CustomerAddressInput,
    CustomerAddressesDocument,
    useCreateCustomerAddressMutation,
    useDeleteCustomerAddressMutation,
    useUpdateCustomerAddressMutation,
} from '../../../types/front.graphql';
import type { AddressFormType } from '../components/address-form';
import { getInputVariable } from '../utils/create-update-address-form.utils';

export interface UseCreateUpdateAddressFormMutationProps {
    shippingAddress?: CustomerAddress;
    showFailedDialog: (message: string) => void;
    showSuccessDialog: (type: 'delete' | 'create' | 'update') => void;
    onSuccess?: () => void;
}

export const useCreateUpdateAddressFormMutation = ({
    shippingAddress,
    showFailedDialog,
    onSuccess,
}: UseCreateUpdateAddressFormMutationProps) => {
    const { t } = useTranslation('customer');

    const [createCustomerAddressMutation, { loading: creatingLoading }] = useCreateCustomerAddressMutation({
        onCompleted: () => {
            onSuccess?.();
        },
        onError: (error: ApolloError) => {
            showFailedDialog(error?.message ?? t('customer_text_create_address_fail'));
        },
    });

    const [updateCustomerAddressMutation, { loading: updatingLoading }] = useUpdateCustomerAddressMutation({
        onCompleted: () => {
            onSuccess?.();
        },
        onError: (error: ApolloError) => {
            showFailedDialog(error?.message ?? t('customer_text_update_address_fail'));
        },
    });

    const [deleteCustomerAddressMutation, { loading: deletingLoading }] = useDeleteCustomerAddressMutation({
        onCompleted: () => {
            onSuccess?.();
        },
        onError: (error: ApolloError) => {
            showFailedDialog(error?.message ?? t('customer_text_delete_address_fail'));
        },
    });

    const handleSubmit = useCallback(
        (data: AddressFormType) => {
            if (shippingAddress && shippingAddress?.id) {
                const inputData = getInputVariable(data, shippingAddress);
                return updateCustomerAddressMutation({
                    variables: {
                        updateCustomerAddressId: shippingAddress.id,
                        input: inputData as CustomerAddressInput,
                    },
                    refetchQueries: [CustomerAddressesDocument],
                });
            } else {
                const inputData = getInputVariable(data, shippingAddress);
                return createCustomerAddressMutation({
                    variables: {
                        input: inputData as CustomerAddressInput,
                    },
                    refetchQueries: [CustomerAddressesDocument],
                });
            }
        },
        [createCustomerAddressMutation, updateCustomerAddressMutation, shippingAddress]
    );

    const handleDeleteCustomerAddress = useCallback(
        (deleteCustomerAddressId: number) => {
            if (shippingAddress && shippingAddress?.id) {
                return deleteCustomerAddressMutation({
                    variables: {
                        deleteCustomerAddressId,
                    },
                    refetchQueries: [CustomerAddressesDocument],
                });
            }
            return;
        },
        [deleteCustomerAddressMutation, shippingAddress]
    );

    const submittingLoading = useMemo(() => updatingLoading || creatingLoading, [updatingLoading, creatingLoading]);

    const isButtonDisabled = useMemo(() => submittingLoading || deletingLoading, [submittingLoading, deletingLoading]);

    return {
        onSubmit: handleSubmit,
        deleteCustomerAddressMutation,
        submittingLoading,
        deletingLoading,
        isButtonDisabled,
        onDeleteCustomerAddress: handleDeleteCustomerAddress,
    };
};
